// src/components/CheckoutPage.jsx

import { useState, useEffect, useCallback, useMemo } from "react";
import { useCart } from "../context/CartContext";
import { useNavigate, useLocation } from "react-router-dom";
import BackButton from "./BackButton";
import {
  MapPin,
  CreditCard,
  Lock,
  Truck,
  Shield,
  ShoppingBag,
  Home,
  Check,
} from "lucide-react";
import { CircularProgress, Snackbar, Alert } from "@mui/material";
import { getUserProfile } from "../utils/auth";
import { placeOrder } from "../api/checkoutApi";
import paypalLogo from "../images/PayPal.svg.webp";
import applePayLogo from "../images/Applepay.png";
import {
  mapBillingToCheckoutFields,
  loadCheckoutPrefill,
  clearCheckoutPrefill,
  DEFAULT_CHECKOUT_PHONE,
} from "../utils/checkoutPrefill";
import { trackCheckoutStep } from "../services/uiAgentSync";
import {
  normalizeAgentLines,
  normalizeAgentTotals,
  DEFAULT_AGENT_TAX_PERCENT,
  PRICE_MATCH_ADJUSTMENT_LABEL,
} from "../utils/agentBillingPayload";

function roundMoney(n) {
  return Math.round(parseFloat(String(n ?? 0)) * 100) / 100;
}

function AgentLinePrice({ item, formatPrice }) {
  const lineTotal =
    item.lineTotal != null
      ? item.lineTotal
      : (parseFloat(item.price) || 0) * (item.quantity ?? 1);

  if (item.priceMatched && item.originalLineTotal != null) {
    return (
      <div className="flex flex-col items-end shrink-0">
        <span className="text-[10.5px] ob-ink3 line-through ob-tabnum">
          ${formatPrice(item.originalLineTotal)}
        </span>
        <span
          className="text-[13px] font-bold ob-tabnum"
          style={{ color: "var(--ob-ok)" }}
        >
          ${formatPrice(lineTotal)}
        </span>
      </div>
    );
  }

  return (
    <p className="text-[13px] font-bold ob-tabnum ob-ink shrink-0">
      ${formatPrice(lineTotal)}
    </p>
  );
}

/** One section shell, so every card on the page has the same rhythm. */
function Section({ icon, title, sub, tint, children }) {
  return (
    <section className="ob-panel p-5 sm:p-6">
      <div className="flex items-center gap-3 mb-5">
        <span className="ob-cosection-glyph" style={{ "--ob-tint": tint }}>
          {icon}
        </span>
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold ob-ink leading-tight">
            {title}
          </h2>
          <p className="text-[11.5px] ob-ink3 mt-0.5">{sub}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="ob-lbl">
        {label}
        {required && <span style={{ color: "var(--ob-err)" }}> *</span>}
      </label>
      {children}
    </div>
  );
}

export default function CheckoutPage() {
  const { cartItems, totalPrice, clearCart, replaceCartItemsFromAgent } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const userProfile = getUserProfile();

  // Agent checkout: hydrate UI cart from navigate state if context is still empty
  // (setState from onLastResponse may not have flushed before navigation).
  useEffect(() => {
    const cartData = location.state?.cartData;
    if (Array.isArray(cartData) && cartData.length > 0 && cartItems.length === 0) {
      replaceCartItemsFromAgent(cartData);
    }
  }, [location.state, cartItems.length, replaceCartItemsFromAgent]);

  // UI → Agent sync: single-page checkout — report entry as billing_review
  useEffect(() => {
    trackCheckoutStep("billing_review");
  }, []);

  const [shippingInfo, setShippingInfo] = useState({
    firstName: userProfile?.customer_name?.split(" ")[0] || "",
    lastName: userProfile?.customer_name?.split(" ").slice(1).join(" ") || "",
    email: userProfile?.email || "",
    phone: userProfile?.phone || DEFAULT_CHECKOUT_PHONE,
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
  });

  const [paymentMethod, setPaymentMethod] = useState("card");
  const [cardDetails, setCardDetails] = useState({
    cardNumber: "",
    cardholderName: "",
    expirationDate: "",
    cvv: "",
  });

  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [billingAddress, setBillingAddress] = useState({
    firstName: "",
    lastName: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
  });

  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const applyBillingPrefill = useCallback((billing) => {
    const mapped = mapBillingToCheckoutFields(billing);
    if (!mapped) return;

    setShippingInfo((prev) => ({ ...prev, ...mapped.shipping }));
    setBillingAddress((prev) => ({ ...prev, ...mapped.billing }));
    setSameAsShipping(true);
    if (mapped.card?.cardNumber || mapped.card?.cardholderName) {
      setCardDetails((prev) => ({ ...prev, ...mapped.card }));
      setPaymentMethod("card");
    }
  }, []);

  useEffect(() => {
    const billingFromNav = location.state?.billingDetails;
    const billingFromAgent = billingFromNav || loadCheckoutPrefill();
    if (billingFromAgent) {
      applyBillingPrefill(billingFromAgent);
    }
  }, [location.state, applyBillingPrefill]);

  const isAgentCheckout = Boolean(
    location.state?.isAgentCheckout ||
      (location.state?.fromChat &&
        (Array.isArray(location.state?.lines) ||
          location.state?.totals != null))
  );

  const agentLines = useMemo(
    () => normalizeAgentLines(location.state?.lines),
    [location.state?.lines]
  );

  const agentTotals = useMemo(
    
    () => normalizeAgentTotals(location.state?.totals),
    [location.state?.totals]
  );

  const summaryItems = agentLines.length > 0 ? agentLines : cartItems;

  const formatPrice = (price) => {
    const num = typeof price === "string" ? parseFloat(price) : price;
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  // Bill: agent totals from backend when present; otherwise cart + 5% tax + optional promo.
  const subtotal = agentTotals ? agentTotals.originalSubtotal : totalPrice;
  
  const discount = agentTotals
    ? agentTotals.discountTotal
    : promoApplied
      ? subtotal * 0.1
      : 0;
  const taxableSubtotal = Math.max(0, subtotal - discount);
  const taxPercent = agentTotals
    ? agentTotals.taxPercent || DEFAULT_AGENT_TAX_PERCENT
    : DEFAULT_AGENT_TAX_PERCENT;
  const tax = agentTotals
    ? agentTotals.tax
    : roundMoney(taxableSubtotal * DEFAULT_AGENT_TAX_PERCENT / 100);
  const total = agentTotals
    ? agentTotals.total
    : roundMoney(taxableSubtotal + tax);

  const showToast = (message, severity = "error") => {
    setToast({ open: true, message, severity });
  };

  const handleInputChange = (section, field, value) => {
    if (section === "shipping") {
      setShippingInfo((prev) => ({ ...prev, [field]: value }));
      if (sameAsShipping) {
        setBillingAddress((prev) => ({ ...prev, [field]: value }));
      }
    } else if (section === "card") {
      setCardDetails((prev) => ({ ...prev, [field]: value }));
    } else if (section === "billing") {
      setBillingAddress((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSameAsShippingChange = (checked) => {
    setSameAsShipping(checked);
    if (checked) {
      setBillingAddress({
        firstName: shippingInfo.firstName,
        lastName: shippingInfo.lastName,
        streetAddress: shippingInfo.streetAddress,
        city: shippingInfo.city,
        state: shippingInfo.state,
        zipCode: shippingInfo.zipCode,
        country: shippingInfo.country,
      });
    }
  };

  const handleApplyPromo = () => {
    if (promoCode.trim().toUpperCase() === "SAVE10") {
      setPromoApplied(true);
      showToast("Promo code applied! 10% discount.", "success");
    } else {
      setPromoApplied(false);
      showToast("Invalid promo code.");
    }
  };

  const validateForm = () => {
    const shippingRequired = [
      ["firstName", "First name"],
      ["lastName", "Last name"],
      ["email", "Email"],
      ["phone", "Phone"],
      ["streetAddress", "Street address"],
      ["city", "City"],
      ["state", "State"],
      ["zipCode", "ZIP code"],
    ];
    for (const [field, label] of shippingRequired) {
      if (!shippingInfo[field]?.trim()) return `${label} is required`;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shippingInfo.email)) {
      return "Please enter a valid email address";
    }
    if (paymentMethod === "card") {
      if (!cardDetails.cardNumber?.trim()) return "Card number is required";
      if (!cardDetails.cardholderName?.trim()) return "Cardholder name is required";
      if (!cardDetails.expirationDate?.trim()) return "Expiration date is required";
      if (!cardDetails.cvv?.trim()) return "CVV is required";
    }
    if (!sameAsShipping) {
      const billingRequired = [
        ["firstName", "Billing first name"],
        ["lastName", "Billing last name"],
        ["streetAddress", "Billing street address"],
        ["city", "Billing city"],
        ["state", "Billing state"],
        ["zipCode", "Billing ZIP code"],
      ];
      for (const [field, label] of billingRequired) {
        if (!billingAddress[field]?.trim()) return `${label} is required`;
      }
    }
    return null;
  };

  const handlePlaceOrder = async () => {
    const validationError = validateForm();
    if (validationError) {
      showToast(validationError);
      return;
    }

    if (!userProfile?.customer_id) {
      showToast("Please log in to place an order");
      return;
    }

    const effectiveBilling = sameAsShipping ? shippingInfo : billingAddress;

    const orderPayload = {
      customer_id: userProfile.customer_id,
      shipping_address: {
        first_name: shippingInfo.firstName,
        last_name: shippingInfo.lastName,
        email: shippingInfo.email,
        phone: shippingInfo.phone || DEFAULT_CHECKOUT_PHONE,
        street_address: shippingInfo.streetAddress,
        city: shippingInfo.city,
        state: shippingInfo.state,
        zip_code: shippingInfo.zipCode,
        country: shippingInfo.country,
      },
      billing_address: {
        first_name: effectiveBilling.firstName,
        last_name: effectiveBilling.lastName,
        street_address: effectiveBilling.streetAddress,
        city: effectiveBilling.city,
        state: effectiveBilling.state,
        zip_code: effectiveBilling.zipCode,
        country: effectiveBilling.country,
      },
      payment_method: paymentMethod,
      ...(paymentMethod === "card" && {
        card_last_four: cardDetails.cardNumber.replace(/\s/g, "").slice(-4),
      }),
      items: cartItems.map((item) => ({
        article_id: String(item.id),
        name: item.name || item.title || "",
        quantity: item.quantity,
        price: item.price,
        size: item.size || null,
        image: item.image_uri || (typeof item.image === "string" && item.image ? item.image : null),
      })),
      subtotal,
      tax,
      shipping: 0,
      discount,
      total,
      promo_code: promoApplied ? promoCode.toUpperCase() : null,
    };

    setLoading(true);
    try {
      const orderResponse = await placeOrder(orderPayload);
      clearCheckoutPrefill();
      showToast("Order placed successfully!", "success");
      // Snapshot cart before clearing for confirmation page
      const orderedItems = [...cartItems];
      try {
        await clearCart({ silent: true });
      } catch (clearErr) {
        console.warn("[Checkout] clearCart after order failed:", clearErr);
      }
      setTimeout(() => {
        navigate("/order-confirmation", {
          state: {
            orderResponse,
            shippingInfo,
            billingAddress: effectiveBilling,
            paymentMethod,
            cardLastFour:
              paymentMethod === "card"
                ? cardDetails.cardNumber.replace(/\s/g, "").slice(-4)
                : null,
            subtotal,
            shipping: 0,
            tax,
            discount,
            total,
            orderedItems,
          },
        });
      }, 600);
    } catch (error) {
      showToast(error.message || "Failed to place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  if (cartItems.length === 0) {
    return (
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8 ob-viewin">
        <BackButton />
        <div className="ob-empty">
          <div className="ob-empty-glyph">
            <ShoppingBag className="w-6 h-6" />
          </div>
          <div className="ob-display text-[27px]" style={{ color: "var(--ob-ink)" }}>
            Nothing to check out
          </div>
          <p className="text-[13px] mt-2 max-w-[320px] mx-auto leading-relaxed">
            Your bag is empty, so there is nothing to pay for yet.
          </p>
          <button
            type="button"
            onClick={() => navigate("/cart")}
            className="ob-btn ob-btn-solid ob-btn-lg mt-6"
          >
            Go to your bag
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8 ob-viewin">
      <BackButton />

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* ── Header ── */}
      <header className="mb-6 mt-2">
        <span className="ob-eyebrow">Checkout</span>
        <h1 className="ob-display text-[clamp(1.8rem,4.2vw,2.5rem)] leading-[1.08] mt-1">
          Almost yours
        </h1>
        {isAgentCheckout && (
          <div className="ob-chip ob-chip-accent mt-3">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-[13px] h-[13px]"
            >
              <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9Z" />
            </svg>
            Prepared by Orbiee — check it over and confirm
          </div>
        )}
      </header>

      {/* Trust strip */}
      <div className="ob-truststrip mb-6">
        {[
          { Icon: Lock, label: "Secure payment", tint: "var(--ob-agent)" },
          { Icon: Truck, label: "Free shipping", tint: "var(--ob-ok)" },
          { Icon: Shield, label: "SSL encrypted", tint: "var(--ob-warn)" },
        ].map(({ Icon, label, tint }) => (
          <div key={label} className="flex items-center gap-2">
            <Icon className="w-[15px] h-[15px]" style={{ color: tint }} />
            <span className="text-[12px] font-medium ob-ink2">{label}</span>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr,384px] gap-5 items-start">
        {/* ══ Left ══ */}
        <div className="space-y-4">
          {/* Shipping */}
          <Section
            icon={<MapPin className="w-[17px] h-[17px]" />}
            title="Shipping information"
            sub="Where should we deliver your order?"
            tint="var(--ob-agent)"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {[
                { label: "First name", field: "firstName", type: "text" },
                { label: "Last name", field: "lastName", type: "text" },
                { label: "Email", field: "email", type: "email" },
                { label: "Phone", field: "phone", type: "tel" },
              ].map(({ label, field, type }) => (
                <Field key={field} label={label} required>
                  <input
                    type={type}
                    value={shippingInfo[field]}
                    onChange={(e) =>
                      handleInputChange("shipping", field, e.target.value)
                    }
                    className="ob-field"
                  />
                </Field>
              ))}

              <div className="md:col-span-2">
                <Field label="Street address" required>
                  <input
                    type="text"
                    value={shippingInfo.streetAddress}
                    onChange={(e) =>
                      handleInputChange("shipping", "streetAddress", e.target.value)
                    }
                    className="ob-field"
                  />
                </Field>
              </div>

              {[
                { label: "City", field: "city" },
                { label: "State", field: "state" },
                { label: "ZIP code", field: "zipCode" },
              ].map(({ label, field }) => (
                <Field key={field} label={label} required>
                  <input
                    type="text"
                    value={shippingInfo[field]}
                    onChange={(e) =>
                      handleInputChange("shipping", field, e.target.value)
                    }
                    className="ob-field"
                  />
                </Field>
              ))}

              <Field label="Country">
                <select
                  value={shippingInfo.country}
                  onChange={(e) =>
                    handleInputChange("shipping", "country", e.target.value)
                  }
                  className="ob-field"
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Australia">Australia</option>
                </select>
              </Field>
            </div>
          </Section>

          {/* Payment */}
          <Section
            icon={<CreditCard className="w-[17px] h-[17px]" />}
            title="Payment method"
            sub="Choose how you would like to pay"
            tint="var(--ob-ok)"
          >
            <div className="grid grid-cols-3 gap-2 mb-5">
              {[
                {
                  id: "card",
                  label: "Card",
                  icon: <CreditCard className="w-[18px] h-[18px]" />,
                },
                {
                  id: "paypal",
                  label: "PayPal",
                  icon: (
                    <img
                      src={paypalLogo}
                      alt=""
                      className="h-[18px] w-auto object-contain"
                    />
                  ),
                },
                {
                  id: "applepay",
                  label: "Apple Pay",
                  icon: (
                    <img
                      src={applePayLogo}
                      alt=""
                      className="h-[18px] w-auto object-contain"
                    />
                  ),
                },
              ].map(({ id, label, icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPaymentMethod(id)}
                  className={`ob-paytab ${paymentMethod === id ? "ob-sel" : ""}`}
                  aria-pressed={paymentMethod === id}
                >
                  {icon}
                  <span>{label}</span>
                </button>
              ))}
            </div>

            {paymentMethod === "paypal" && (
              <div className="ob-payslate ob-fadein">
                <img
                  src={paypalLogo}
                  alt="PayPal"
                  className="h-12 w-auto object-contain mx-auto"
                />
                <p className="text-center text-[12.5px] ob-ink2 mt-3 leading-relaxed">
                  You will be securely redirected to PayPal to finish paying.
                </p>
                <div className="flex flex-col gap-1.5 mt-4">
                  <div className="flex items-center gap-2 text-[12px] ob-ink2">
                    <Check
                      className="w-[14px] h-[14px]"
                      style={{ color: "var(--ob-ok)" }}
                    />
                    Secure PayPal checkout
                  </div>
                  <div className="flex items-center gap-2 text-[12px] ob-ink2">
                    <Lock
                      className="w-[14px] h-[14px]"
                      style={{ color: "var(--ob-agent)" }}
                    />
                    Your payment details are never shared
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handlePlaceOrder}
                  disabled={loading}
                  className="ob-btn ob-btn-solid ob-btn-lg ob-btn-block mt-4"
                >
                  {loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Continue with PayPal"
                  )}
                </button>
              </div>
            )}

            {paymentMethod === "applepay" && (
              <div className="ob-payslate ob-fadein">
                <img
                  src={applePayLogo}
                  alt="Apple Pay"
                  className="h-12 w-auto object-contain mx-auto"
                />
                <p className="text-center text-[12.5px] ob-ink2 mt-3 leading-relaxed">
                  Confirm with Touch ID or Face ID — nothing else to type.
                </p>
                <div className="flex flex-col gap-1.5 mt-4">
                  <div className="flex items-center gap-2 text-[12px] ob-ink2">
                    <Check
                      className="w-[14px] h-[14px]"
                      style={{ color: "var(--ob-ok)" }}
                    />
                    Biometric authentication
                  </div>
                  <div className="flex items-center gap-2 text-[12px] ob-ink2">
                    <Lock
                      className="w-[14px] h-[14px]"
                      style={{ color: "var(--ob-agent)" }}
                    />
                    End-to-end encrypted
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handlePlaceOrder}
                  disabled={loading}
                  className="ob-btn ob-btn-solid ob-btn-lg ob-btn-block mt-4"
                >
                  {loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Pay with Apple Pay"
                  )}
                </button>
              </div>
            )}

            {paymentMethod === "card" && (
              <div className="space-y-3.5 ob-fadein">
                <Field label="Card number" required>
                  <input
                    type="text"
                    value={cardDetails.cardNumber}
                    onChange={(e) =>
                      handleInputChange("card", "cardNumber", e.target.value)
                    }
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                    className="ob-field ob-tabnum"
                    inputMode="numeric"
                    autoComplete="cc-number"
                  />
                </Field>
                <Field label="Cardholder name" required>
                  <input
                    type="text"
                    value={cardDetails.cardholderName}
                    onChange={(e) =>
                      handleInputChange("card", "cardholderName", e.target.value)
                    }
                    placeholder="Name as printed on the card"
                    className="ob-field"
                    autoComplete="cc-name"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3.5">
                  <Field label="Expires" required>
                    <input
                      type="text"
                      value={cardDetails.expirationDate}
                      onChange={(e) =>
                        handleInputChange("card", "expirationDate", e.target.value)
                      }
                      placeholder="MM/YY"
                      maxLength={5}
                      className="ob-field ob-tabnum"
                      inputMode="numeric"
                      autoComplete="cc-exp"
                    />
                  </Field>
                  <Field label="CVV" required>
                    <input
                      type="password"
                      value={cardDetails.cvv}
                      onChange={(e) =>
                        handleInputChange("card", "cvv", e.target.value)
                      }
                      placeholder="•••"
                      maxLength={4}
                      className="ob-field ob-tabnum"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                    />
                  </Field>
                </div>
              </div>
            )}
          </Section>

          {/* Billing */}
          <Section
            icon={<Home className="w-[17px] h-[17px]" />}
            title="Billing address"
            sub="Where should we send the invoice?"
            tint="var(--ob-agent)"
          >
            <label className="ob-checkrow">
              <input
                type="checkbox"
                checked={sameAsShipping}
                onChange={(e) => handleSameAsShippingChange(e.target.checked)}
              />
              <span className="ob-checkbox" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-3 h-3"
                >
                  <path d="m4 12.5 5 5L20 6.5" />
                </svg>
              </span>
              <span className="text-[12.5px] font-medium ob-ink">
                Same as my shipping address
              </span>
            </label>

            {!sameAsShipping && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mt-4 ob-fadein">
                {[
                  { label: "First name", field: "firstName" },
                  { label: "Last name", field: "lastName" },
                ].map(({ label, field }) => (
                  <Field key={field} label={label} required>
                    <input
                      type="text"
                      value={billingAddress[field]}
                      onChange={(e) =>
                        handleInputChange("billing", field, e.target.value)
                      }
                      className="ob-field"
                    />
                  </Field>
                ))}

                <div className="md:col-span-2">
                  <Field label="Street address" required>
                    <input
                      type="text"
                      value={billingAddress.streetAddress}
                      onChange={(e) =>
                        handleInputChange("billing", "streetAddress", e.target.value)
                      }
                      className="ob-field"
                    />
                  </Field>
                </div>

                {[
                  { label: "City", field: "city" },
                  { label: "State", field: "state" },
                  { label: "ZIP code", field: "zipCode" },
                ].map(({ label, field }) => (
                  <Field key={field} label={label} required>
                    <input
                      type="text"
                      value={billingAddress[field]}
                      onChange={(e) =>
                        handleInputChange("billing", field, e.target.value)
                      }
                      className="ob-field"
                    />
                  </Field>
                ))}

                <Field label="Country">
                  <select
                    value={billingAddress.country}
                    onChange={(e) =>
                      handleInputChange("billing", "country", e.target.value)
                    }
                    className="ob-field"
                  >
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Australia">Australia</option>
                  </select>
                </Field>
              </div>
            )}
          </Section>
        </div>

        {/* ══ Right: order summary ══ */}
        <aside
          className="ob-panel p-5 lg:sticky"
          style={{ top: "calc(var(--ob-topbar-h) + 16px)" }}
        >
          <h2 className="text-[15px] font-semibold ob-ink mb-4">
            Order summary
          </h2>

          <div className="flex flex-col gap-2.5 max-h-64 overflow-y-auto ob-scroll -mr-2 pr-2">
            {summaryItems.map((item, index) => (
              <div
                key={item.cartLineUid || item.id || item.product_id || index}
                className="flex items-center gap-3"
              >
                <div className="ob-orderthumb">
                  {item.image_uri || item.image ? (
                    <img
                      src={
                        item.image_uri ||
                        (Array.isArray(item.image) ? item.image[0] : item.image)
                      }
                      alt=""
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center ob-ink3">
                      <ShoppingBag className="w-4 h-4" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12.5px] font-semibold ob-ink truncate">
                    {item.title || item.name || "Item"}
                  </p>
                  <p className="text-[11px] ob-ink3 mt-0.5">
                    {item.size ? `Size ${item.size} · ` : ""}Qty {item.quantity}
                  </p>
                </div>
                <AgentLinePrice item={item} formatPrice={formatPrice} />
              </div>
            ))}
          </div>

          {/* Promo — manual checkout only */}
          {!isAgentCheckout && (
            <div className="mt-5 pt-4 ob-hair-t">
              <label className="ob-lbl">Promo code</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    setPromoApplied(false);
                  }}
                  placeholder="e.g. SAVE10"
                  className="ob-field flex-1 min-w-0"
                />
                <button
                  type="button"
                  onClick={handleApplyPromo}
                  disabled={!promoCode.trim() || promoApplied}
                  className="ob-btn ob-btn-ghost shrink-0"
                >
                  {promoApplied ? "Applied" : "Apply"}
                </button>
              </div>
              {promoApplied && (
                <div className="ob-chip ob-chip-ok mt-2">
                  <Check className="w-3 h-3" /> 10% discount applied
                </div>
              )}
            </div>
          )}

          {/* Breakdown */}
          <div className="mt-5 pt-4 ob-hair-t">
            <div className="flex justify-between text-[12.5px] ob-ink2 py-1.5">
              <span>Subtotal</span>
              <span className="font-semibold ob-tabnum ob-ink">
                ${formatPrice(subtotal)}
              </span>
            </div>
            <div className="flex justify-between text-[12.5px] ob-ink2 py-1.5">
              <span>Tax ({taxPercent}%)</span>
              <span className="font-semibold ob-tabnum ob-ink">
                ${formatPrice(tax)}
              </span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-[12.5px] ob-ink2 py-1.5">
                <span>{PRICE_MATCH_ADJUSTMENT_LABEL}</span>
                <span
                  className="font-semibold ob-tabnum"
                  style={{ color: "var(--ob-ok)" }}
                >
                  -${formatPrice(discount)}
                </span>
              </div>
            )}

            <div className="flex justify-between items-baseline pt-3 mt-2 ob-hair-t">
              <span className="text-[15px] font-bold ob-ink">Total</span>
              <span className="text-[22px] font-bold ob-tabnum ob-ink">
                ${formatPrice(total)}
              </span>
            </div>
          </div>

          {paymentMethod === "card" && (
            <button
              type="button"
              onClick={handlePlaceOrder}
              disabled={loading}
              className="ob-btn ob-btn-solid ob-btn-lg ob-btn-block mt-4"
            >
              {loading ? <CircularProgress size={19} color="inherit" /> : "Place order"}
            </button>
          )}

          <p className="text-[10.5px] ob-ink3 text-center mt-3 leading-relaxed">
            By placing your order you agree to our terms and conditions.
          </p>

          {/* Reassurance, not an alert: a hairline, a small-caps line and one
              sentence — the same promise as the old tinted callout, without
              shouting it in warning colours next to the pay button. */}
          <div className="ob-paynote">
            <span className="ob-paynote-rule" aria-hidden="true" />
            <span className="ob-paynote-label">
              <Shield className="w-[12px] h-[12px]" />
              Secure checkout
            </span>
            <span className="ob-paynote-rule" aria-hidden="true" />
          </div>
          <p className="ob-paynote-sub">
            Your payment details are encrypted end to end.
          </p>
        </aside>
      </div>
    </div>
  );
}
